import React, {useRef, useState, useEffect} from 'react';
import {Body, Button} from '@lightricks/react-design-system';
import Modal from '@/components/modal';
import uploadAsset from '@/services/file-upload/uploadAsset';
import translate from '@/utils/translate';
import {VersionChat} from '../../messages';
import validateFile from '../../validateFile';
import useCreateSubmission from '@/hooks/queries/mutations/use-create-submission';
import useReviseSubmission from '@/hooks/queries/mutations/use-revise-submission';
import {DeliverableSlot} from '../../types';
import {
  submissionIdFromMutation,
  useProcessingPreviewStore,
} from '../../processingPreviewStore';
import {trackDeliverables, captureDeliverablesError} from '../../telemetry';
import submissionErrorMessage from '../../submissionErrorMessage';
import styles from './SubmissionSheet.module.scss';

// Noun for the file-picker CTA, by deliverable kind (mixed_media uses the dedicated editor).
const PICK_NOUN: Record<string, string> = {
  photo: 'photo',
  video: 'video',
  blog: 'file',
};

// File-dialog filter per kind (validation is the real gate; this just guides the picker).
const ACCEPT_BY_KIND: Record<string, string> = {
  photo: 'image/*',
  video: 'video/*',
  blog: 'image/*,application/pdf',
};

interface Props {
  open: boolean;
  slot: DeliverableSlot;
  gigId: string;
  initialVersionId?: string | null;
  onClose: () => void;
}

export default function SubmissionSheet({
  open,
  slot,
  gigId,
  initialVersionId,
  onClose,
}: Props) {
  const isRevise = slot.state === 'edits_requested';
  const isView =
    slot.state === 'pending' ||
    slot.state === 'approved' ||
    slot.state === 'archived';
  let sheetMode = 'upload';
  if (isView) sheetMode = 'view';
  else if (isRevise) sheetMode = 'revise';
  let viewStatusLabel = translate('In review');
  if (slot.state === 'approved') viewStatusLabel = translate('Approved');
  else if (slot.state === 'archived') viewStatusLabel = translate('Archived');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState(slot.submission?.note ?? '');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const create = useCreateSubmission(gigId);
  const revise = useReviseSubmission(gigId);
  const rememberPreview = useProcessingPreviewStore((state) => state.remember);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    trackDeliverables('Opened submission sheet', {
      gigId,
      kind: slot.kind,
      mode: sheetMode,
      submissionId: slot.submission?.id,
    });
  }, [open, gigId, slot.kind, slot.submission?.id, sheetMode]);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const v = validateFile(f, slot.kind);
    if (!v.valid) {
      trackDeliverables('Submission file rejected', {
        gigId,
        kind: slot.kind,
        reason: v.reason,
      });
      setError(v.reason);
      return;
    }
    setError(null);
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  }

  const onSubmit = async () => {
    if (busyRef.current) return;
    if (!caption.trim()) {
      setError(translate('A caption is required.'));
      return;
    }
    if (!isRevise && !file) {
      setError(translate('Choose a file to upload.'));
      return;
    }
    busyRef.current = true;
    setIsSubmitting(true);
    try {
      let sourceUrl: string | undefined;
      if (file) {
        controllerRef.current = new AbortController();
        setProgress(0);
        ({sourceUrl} = await uploadAsset(file, {
          onProgress: (pct) => {
            if (!controllerRef.current?.signal.aborted) setProgress(pct);
          },
          signal: controllerRef.current.signal,
        }));
        setProgress(null);
      }
      if (isRevise && slot.submission) {
        await revise.mutateAsync({
          submissionId: slot.submission.id,
          payload: {sourceUrl, note: caption},
        });
        if (file) rememberPreview(slot.submission.id, file);
      } else {
        const created = await create.mutateAsync({
          kind: slot.kind,
          deliverableKind: slot.deliverableKind,
          sourceUrl,
          note: caption,
        });
        const createdId = submissionIdFromMutation(created);
        if (createdId && file) rememberPreview(createdId, file);
      }
      trackDeliverables(
        isRevise ? 'Submission revised' : 'Submission created',
        {
          gigId,
          kind: slot.kind,
          submissionId: slot.submission?.id,
        }
      );
      onClose();
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
        trackDeliverables('Submission upload canceled', {
          gigId,
          kind: slot.kind,
        });
      } else {
        captureDeliverablesError(err, 'Submission failed', {
          gigId,
          kind: slot.kind,
          mode: sheetMode,
          submissionId: slot.submission?.id,
        });
        setError(
          submissionErrorMessage(err, translate('Something went wrong.'))
        );
      }
      setProgress(null);
    } finally {
      setIsSubmitting(false);
      busyRef.current = false;
    }
  };

  const onCancelUpload = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setProgress(null);
  };

  const isUploading = progress !== null;
  const isBusy =
    isUploading || isSubmitting || create.isLoading || revise.isLoading;
  busyRef.current = isBusy;

  const handleClose = () => {
    if (busyRef.current) return;
    onClose();
  };

  let title: string;
  if (isView) {
    title = translate('Submission');
  } else if (isRevise) {
    title = `${translate('Submit revision')} · ${slot.index}`;
  } else {
    title = `${translate('Upload')} · ${slot.index}`;
  }

  const existingUrl = isRevise
    ? slot.submission?.thumbnailUrl ?? slot.submission?.contentUrl ?? null
    : null;
  const noun = PICK_NOUN[slot.kind] ?? 'file';

  let pickLabel: string;
  if (file) {
    pickLabel = translate(`Choose a different ${noun}`);
  } else if (isRevise) {
    pickLabel = translate(`Replace ${noun}`);
  } else {
    pickLabel = translate(`Choose a ${noun}`);
  }

  let submitLabel = translate('Submit');
  if (isUploading) {
    submitLabel = translate('Uploading…');
  } else if (isRevise) {
    submitLabel = translate('Resubmit revision');
  }

  return (
    <Modal open={open} onClose={handleClose} title={title} bottomSheet>
      {isRevise && slot.submission?.brandMessage && (
        <div className={styles.feedback} data-testid="revision-message">
          <Body size="xs" className={styles.feedbackLabel}>
            {translate('The brand requested changes')}
          </Body>
          <Body size="sm" className={styles.feedbackMessage}>
            {slot.submission.brandMessage}
          </Body>
        </div>
      )}
      {isView ? (
        <div className={styles.viewContent}>
          {(() => {
            const mediaUrl =
              slot.submission?.thumbnailUrl ?? slot.submission?.contentUrl;
            if (!mediaUrl) {
              return (
                <div className={styles.noPreview}>
                  <Body size="sm">{translate('No preview available')}</Body>
                </div>
              );
            }
            return slot.kind === 'video' ? (
              <video
                src={slot.submission?.contentUrl ?? undefined}
                poster={slot.submission?.thumbnailUrl ?? undefined}
                controls
                className={styles.preview}
              >
                <track kind="captions" />
              </video>
            ) : (
              <img
                src={mediaUrl}
                alt={translate('Your submission')}
                className={styles.preview}
              />
            );
          })()}
          {slot.submission?.note && (
            <Body size="sm" className={styles.submissionNote}>
              {slot.submission.note}
            </Body>
          )}
          <div className={styles.statusLine}>
            <span
              className={`${styles.pill} ${
                slot.state === 'approved'
                  ? styles.pillApproved
                  : styles.pillPending
              }`}
            >
              {viewStatusLabel}
            </span>
          </div>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            data-testid="file-input"
            type="file"
            accept={ACCEPT_BY_KIND[slot.kind]}
            hidden
            onChange={onPick}
          />

          {previewUrl && (
            <div className={styles.filePreview}>
              {file?.type.startsWith('video/') ? (
                <video
                  src={previewUrl}
                  className={styles.previewMedia}
                  muted
                  playsInline
                >
                  <track kind="captions" />
                </video>
              ) : (
                <img
                  src={previewUrl}
                  alt={translate('Selected file preview')}
                  className={styles.previewMedia}
                />
              )}
              <Body size="xs" className={styles.fileName}>
                {file?.name}
              </Body>
              <Button
                appearance="neutral"
                mode="plain"
                size="medium"
                onClick={() => {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                  setFile(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
              >
                {translate('Remove')}
              </Button>
            </div>
          )}
          {!previewUrl && existingUrl && (
            <div className={styles.filePreview}>
              {slot.kind === 'video' ? (
                <video
                  src={slot.submission?.contentUrl ?? undefined}
                  poster={slot.submission?.thumbnailUrl ?? undefined}
                  className={styles.previewMedia}
                  controls
                >
                  <track kind="captions" />
                </video>
              ) : (
                <img
                  src={existingUrl}
                  alt={translate('Your current submission')}
                  className={styles.previewMedia}
                />
              )}
              <Body size="xs" className={styles.fileName}>
                {translate('Current file')}
              </Body>
            </div>
          )}

          <button
            type="button"
            data-testid="file-pick-btn"
            className={styles.pickZone}
            onClick={() => inputRef.current?.click()}
          >
            <span className={styles.pickPlus} aria-hidden="true">
              +
            </span>
            {pickLabel}
          </button>

          <textarea
            className={styles.caption}
            placeholder={translate('Caption (required)')}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
          {progress !== null && (
            <div className={styles.progress}>
              <div className={styles.bar} style={{width: `${progress}%`}} />
              <Button
                appearance="neutral"
                mode="plain"
                size="medium"
                onClick={onCancelUpload}
              >
                {translate('Cancel')}
              </Button>
            </div>
          )}
          {error && (
            <Body size="xs" className={styles.error}>
              {error}
            </Body>
          )}
          <Button
            testID="submit-submission"
            appearance="brand"
            mode="filled"
            size="large"
            disabled={isBusy}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </>
      )}
      {(initialVersionId || slot.submission?.latestVersionId) && (
        <VersionChat
          versionId={
            initialVersionId ?? (slot.submission?.latestVersionId as string)
          }
        />
      )}
    </Modal>
  );
}
